import { ThemeEvents } from '@theme/events';

/**
 * <innov-variant-cards>
 *
 * Card radios (format, trainer) that drive the theme's own <variant-picker> in the
 * same section instead of selecting variants themselves. Each radio carries the
 * option value ids of an existing variant:
 *   data-primary-value-id   → option 1 (format)
 *   data-secondary-value-id → option 2 (trainer)
 * Both native inputs are checked, then a `change` is dispatched so the theme runs its
 * usual flow (fetch, price, stock, gallery, form id, `variant:update`).
 * Only existing, available combinations are rendered, so impossible ones can't be picked.
 */
class InnovVariantCards extends HTMLElement {
  #pending = false;

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    this.addEventListener('change', this.#handleChange);
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    event.stopPropagation();

    const picker = this.#section?.querySelector('variant-picker');
    if (!picker) return;

    const secondary = this.#select(picker, input.dataset.secondaryValueId);
    const primary = this.#select(picker, input.dataset.primaryValueId);
    const trigger = primary || secondary;
    if (!trigger) return;

    this.#pending = true;
    trigger.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Checks the native input (buttons style) or option (dropdown style) for an option value id.
   * @param {Element} picker
   * @param {string | undefined} valueId
   * @returns {HTMLInputElement | HTMLSelectElement | null}
   */
  #select(picker, valueId) {
    if (!valueId) return null;
    const match = picker.querySelector(`[data-option-value-id="${valueId}"]`);

    if (match instanceof HTMLInputElement) {
      match.checked = true;
      return match;
    }

    if (match instanceof HTMLOptionElement && match.parentElement instanceof HTMLSelectElement) {
      match.parentElement.value = match.value;
      return match.parentElement;
    }

    return null;
  }

  /**
   * The theme sets ?variant= from the clicked input, which may predate the second option change.
   * Once the theme resolves the real variant, keep the URL in sync with it.
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    if (!this.#pending) return;
    this.#pending = false;

    const id = /** @type {any} */ (event).detail?.resource?.id;
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(id));
    history.replaceState({}, '', url.toString());
  };
}

if (!customElements.get('innov-variant-cards')) {
  customElements.define('innov-variant-cards', InnovVariantCards);
}
